import React, { useEffect, useState } from "react";
import { getReportDashboard } from "../../api/apiGetrequest";
import MarqueeDisplay from "./Maquee";

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);

  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const today = new Date();
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const startDate = startOfMonth.toISOString().split("T")[0];
        const endDate = today.toISOString().split("T")[0];

        // Set loading to true while fetching
        setLoading(true);

        // Fetch the report dashboard data
        const { result } = await getReportDashboard(startDate, endDate);

        // Set the data once fetched
        setData(result || []);
      } catch (error) {
        console.error("Error fetching data: ", error);
        setData([]); // In case of error, set empty data or handle error accordingly
      } finally {
        // Set loading to false once fetching is done
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to get data by key
  const getDataByKey = (key) => {
    const item = data.find((item) => item.Key === key);
    return item ? item.Value : null;
  };

  // Get specific data sets
  const loadingPointStats = getDataByKey("LoadingPointStats");
  const openShipments = getDataByKey("OpenShipments");
  const overallCounts = getDataByKey("OverallCounts");
  const repairStatus = getDataByKey("RepairStatus");
  const repairCosts = getDataByKey("RepairCosts");
  const fuelUsage = getDataByKey("FuelUsage");
  const licenseStatusCounts = getDataByKey("LicenseStatusCounts");
  const vehicleStatusCounts = getDataByKey("VehicleStatusCounts");
  

  // Calculate total fuel amount
  const totalFuelAmount = fuelUsage
    ? fuelUsage.reduce((sum, station) => sum + station.FuelAmount, 0)
    : 0;

  // Calculate total repair count
  const totalRepairCount = repairStatus
    ? repairStatus.reduce((sum, status) => sum + status.RepairCount, 0)
    : 0;

  // Styles for the dashboard
  const styles = {
    dashboard: {
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      color: "#333",
      backgroundColor: "#f8f9fa",
      minHeight: "100vh",
      padding: "20px",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      padding: "0 12px",
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#1e293b",
      margin: "0",
    },
    date: {
      fontSize: "14px",
      color: "#64748b",
    },
    tabs: {
      display: "flex",
      borderBottom: "1px solid #e2e8f0",
      marginBottom: "24px",
    },
    tab: {
      padding: "12px 20px",
      fontSize: "15px",
      fontWeight: "500",
      color: "#64748b",
      cursor: "pointer",
      borderBottom: "2px solid transparent",
      transition: "all 0.2s ease",
    },
    activeTab: {
      color: "#0f172a",
      borderBottom: "2px solid #3b82f6",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
      gap: "24px",
      marginBottom: "24px",
    },
    card: {
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      padding: "20px",
      transition: "transform 0.2s, box-shadow 0.2s",
    },
    cardHover: {
      transform: "translateY(-5px)",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    cardTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#334155",
      margin: "0",
    },
    largeCard: {
      gridColumn: "span 2",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "16px",
    },
    statItem: {
      display: "flex",
      flexDirection: "column",
    },
    statValue: {
      fontSize: "24px",
      fontWeight: "700",
      color: "#1e293b",
      marginBottom: "4px",
    },
    statLabel: {
      fontSize: "13px",
      color: "#64748b",
    },
    progressBar: {
      height: "8px",
      width: "100%",
      backgroundColor: "#e2e8f0",
      borderRadius: "4px",
      marginTop: "8px",
      overflow: "hidden",
    },
    progressFill: {
      height: "100%",
      backgroundColor: "#3b82f6",
      borderRadius: "4px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    tableHeader: {
      textAlign: "left",
      padding: "12px 16px",
      borderBottom: "1px solid #e2e8f0",
      fontSize: "13px",
      fontWeight: "600",
      color: "#64748b",
    },
    tableCell: {
      padding: "12px 16px",
      borderBottom: "1px solid #e2e8f0",
      fontSize: "14px",
    },
    badge: {
      display: "inline-block",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "500",
    },
    badgeSuccess: {
      backgroundColor: "#dcfce7",
      color: "#166534",
    },
    badgeWarning: {
      backgroundColor: "#fef3c7",
      color: "#92400e",
    },
    badgeDanger: {
      backgroundColor: "#fee2e2",
      color: "#b91c1c",
    },
    // More specific custom styles
    repairStatusGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
      gap: "12px",
    },
    repairStatusItem: {
      backgroundColor: "#f1f5f9",
      padding: "12px",
      borderRadius: "6px",
      textAlign: "center",
    },
    loadingPoints: {
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
    },
    loadingPointItem: {
      flex: "1 1 120px",
      backgroundColor: "#f8fafc",
      padding: "12px",
      borderRadius: "6px",
      border: "1px solid #e2e8f0",
    },
    viewAll: {
      display: "inline-block",
      padding: "8px 16px",
      backgroundColor: "#f1f5f9",
      color: "#334155",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      textAlign: "center",
      cursor: "pointer",
      transition: "background-color 0.2s",
      border: "none",
      marginTop: "16px",
    },
  };

  if (loading) {
    return <div>...loading</div>;
  }

  return (
    <div style={styles.dashboard}>
      {activeTab === "overview" && (
        <>
          <div style={styles.grid}>
            {/* Order Status Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Order Status</h2>
              </div>
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {overallCounts?.Closed || 0}
                  </div>
                  <div style={styles.statLabel}>Closed Orders</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>{overallCounts?.Open || 0}</div>
                  <div style={styles.statLabel}>Open Orders</div>
                </div>
              </div>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${
                      overallCounts
                        ? (overallCounts.Closed /
                            (overallCounts.Closed + overallCounts.Open)) *
                          100
                        : 0
                    }%`,
                  }}
                ></div>
              </div>
            </div>

            {/* Vehicle Status Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Vehicle Status</h2>
              </div>
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {vehicleStatusCounts?.filter(
                      (item) => item.VehicleStatus === "Active"
                    )[0]?.VehicleCount || 0}
                  </div>
                  <div style={styles.statLabel}>Active</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {vehicleStatusCounts?.filter(
                      (item) => item.VehicleStatus === "Due"
                    )[0]?.VehicleCount || 0}
                  </div>
                  <div style={styles.statLabel}>Due</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {vehicleStatusCounts?.filter(
                      (item) => item.VehicleStatus === "Expired"
                    )[0]?.VehicleCount || 0}
                  </div>
                  <div style={styles.statLabel}>Expired</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {vehicleStatusCounts?.filter(
                      (item) => item.VehicleStatus === "Booked"
                    )[0]?.VehicleCount || 0}
                  </div>
                  <div style={styles.statLabel}>Booked</div>
                </div>
              </div>
            </div>

            {/* License Status Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>License Status</h2>
              </div>
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {licenseStatusCounts?.filter(
                      (item) => item.LicenseStatus === "Active"
                    )[0]?.LicenseCount || 0}
                  </div>
                  <div style={styles.statLabel}>Active</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {licenseStatusCounts?.filter(
                      (item) => item.LicenseStatus === "Due"
                    )[0]?.LicenseCount || 0}
                  </div>
                  <div style={styles.statLabel}>Due</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {licenseStatusCounts?.filter(
                      (item) => item.LicenseStatus === "Expired"
                    )[0]?.LicenseCount || 0}
                  </div>
                  <div style={styles.statLabel}>Expired</div>
                </div>
              </div>
            </div>

            {/* Repair Costs Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Repair Costs</h2>
              </div>
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    KSh {repairCosts?.LabourCost?.toLocaleString() || 0}
                  </div>
                  <div style={styles.statLabel}>Labour Cost</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    KSh {repairCosts?.TotalSpareCost?.toLocaleString() || 0}
                  </div>
                  <div style={styles.statLabel}>Spare Parts Cost</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    KSh{" "}
                    {(
                      (repairCosts?.LabourCost || 0) +
                      (repairCosts?.TotalSpareCost || 0)
                    ).toLocaleString()}
                  </div>
                  <div style={styles.statLabel}>Total Repair Cost</div>
                </div>
              </div>
            </div>

            {/* Fuel Usage Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Fuel Usage</h2>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statValue}>
                  KSh {totalFuelAmount.toLocaleString()}
                </div>
                <div style={styles.statLabel}>Total Fuel Cost</div>
              </div>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Station</th>
                    <th style={styles.tableHeader}>Amount (KSh)</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelUsage?.map((station, index) => (
                    <tr key={index}>
                      <td style={styles.tableCell}>{station.StationName}</td>
                      <td style={styles.tableCell}>
                        {station.FuelAmount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Loading Points Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Loading Points</h2>
              </div>
              <div style={styles.loadingPoints}>
                {loadingPointStats?.map((point, index) => (
                  <div key={index} style={styles.loadingPointItem}>
                    <div style={{ fontWeight: "600", marginBottom: "8px" }}>
                      {point.LoadingPointName}
                    </div>
                    <div style={styles.statLabel}>
                      Closed: {point.ClosedOrders}
                    </div>
                    <div style={styles.statLabel}>Open: {point.OpenOrders}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Repair Status Overview Card - Larger card spanning 2 columns */}
          <div style={{ ...styles.card, ...styles.largeCard }}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Repair Status Overview</h2>
            </div>
            <div style={styles.repairStatusGrid}>
              {repairStatus?.map((status, index) => (
                <div key={index} style={styles.repairStatusItem}>
                  <div style={styles.statValue}>{status.RepairCount}</div>
                  <div style={styles.statLabel}>{status.StatusName}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "16px", textAlign: "center" }}>
              <div style={styles.statLabel}>
                Total Repairs: {totalRepairCount}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === "shipments" && (
        <div style={{ ...styles.card, marginBottom: "24px" }}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Open Shipments</h2>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Customer</th>
                  <th style={styles.tableHeader}>Loading Point</th>
                  <th style={styles.tableHeader}>Vehicle Reg</th>
                  <th style={styles.tableHeader}>Status</th>
                </tr>
              </thead>
              <tbody>
                {openShipments?.slice(0, 10).map((shipment, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>{shipment.CustomerName}</td>
                    <td style={styles.tableCell}>
                      {shipment.LoadingPointName}
                    </td>
                    <td style={styles.tableCell}>{shipment.VregNO}</td>
                    <td style={styles.tableCell}>
                      <span style={{ ...styles.badge, ...styles.badgeWarning }}>
                        Open
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {openShipments && openShipments.length > 10 && (
            <button style={styles.viewAll}>View All Shipments</button>
          )}
        </div>
      )}

      {activeTab === "repairs" && (
        <>
          <div style={{ ...styles.card, marginBottom: "24px" }}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Repair Status</h2>
            </div>
            <div style={styles.repairStatusGrid}>
              {repairStatus?.map((status, index) => (
                <div key={index} style={styles.repairStatusItem}>
                  <div style={styles.statValue}>{status.RepairCount}</div>
                  <div style={styles.statLabel}>{status.StatusName}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...styles.card, marginBottom: "24px" }}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Repair Costs</h2>
            </div>
            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <div style={styles.statValue}>
                  KSh {repairCosts?.LabourCost?.toLocaleString() || 0}
                </div>
                <div style={styles.statLabel}>Labour Cost</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statValue}>
                  KSh {repairCosts?.TotalSpareCost?.toLocaleString() || 0}
                </div>
                <div style={styles.statLabel}>Spare Parts Cost</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statValue}>
                  KSh{" "}
                  {(
                    (repairCosts?.LabourCost || 0) +
                    (repairCosts?.TotalSpareCost || 0)
                  ).toLocaleString()}
                </div>
                <div style={styles.statLabel}>Total Repair Cost</div>
              </div>
              <div style={styles.statItem}>
                <div style={styles.statValue}>{totalRepairCount}</div>
                <div style={styles.statLabel}>Total Repairs</div>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === "vehicles" && (
        <>
          <div style={styles.grid}>
            {/* Vehicle Status Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Vehicle Status</h2>
              </div>
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {vehicleStatusCounts?.filter(
                      (item) => item.VehicleStatus === "Active"
                    )[0]?.VehicleCount || 0}
                  </div>
                  <div style={styles.statLabel}>Active</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {vehicleStatusCounts?.filter(
                      (item) => item.VehicleStatus === "Due"
                    )[0]?.VehicleCount || 0}
                  </div>
                  <div style={styles.statLabel}>Due</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {vehicleStatusCounts?.filter(
                      (item) => item.VehicleStatus === "Expired"
                    )[0]?.VehicleCount || 0}
                  </div>
                  <div style={styles.statLabel}>Expired</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {vehicleStatusCounts?.filter(
                      (item) => item.VehicleStatus === "Booked"
                    )[0]?.VehicleCount || 0}
                  </div>
                  <div style={styles.statLabel}>Booked</div>
                </div>
              </div>
            </div>

            {/* License Status Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>License Status</h2>
              </div>
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {licenseStatusCounts?.filter(
                      (item) => item.LicenseStatus === "Active"
                    )[0]?.LicenseCount || 0}
                  </div>
                  <div style={styles.statLabel}>Active</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {licenseStatusCounts?.filter(
                      (item) => item.LicenseStatus === "Due"
                    )[0]?.LicenseCount || 0}
                  </div>
                  <div style={styles.statLabel}>Due</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>
                    {licenseStatusCounts?.filter(
                      (item) => item.LicenseStatus === "Expired"
                    )[0]?.LicenseCount || 0}
                  </div>
                  <div style={styles.statLabel}>Expired</div>
                </div>
              </div>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${
                      licenseStatusCounts
                        ? (licenseStatusCounts.find(
                            (item) => item.LicenseStatus === "Active"
                          )?.LicenseCount /
                            licenseStatusCounts.reduce(
                              (sum, item) => sum + item.LicenseCount,
                              0
                            )) *
                          100
                        : 0
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </>
      )}
      <MarqueeDisplay data={openShipments} />
    </div>
  );
};

export default Dashboard;
